import { useState } from 'react'
import { format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface DatePickerProps {
  value: string // 'yyyy-MM-dd', or '' when unset
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  triggerClassName?: string
  minDate?: string | undefined // 'yyyy-MM-dd' — dates before this are disabled
  fromYear?: number
  toYear?: number
}

// Shared single-date picker (Calendar + Popover) — mirrors the pattern
// already used for salary dates (EmployeeHistory.tsx) and date-range filters
// (ReusableDateRangePicker.tsx), so every date field in the app looks and
// behaves the same instead of mixing in native <input type="date">.
export function DatePicker({
  value,
  onChange,
  placeholder = 'Pick date',
  className,
  triggerClassName,
  minDate,
  fromYear = 2018,
  toYear = 2035,
}: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const selected = value ? new Date(`${value}T00:00:00`) : undefined
  const min = minDate ? new Date(`${minDate}T00:00:00`) : undefined

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          className={cn(
            'w-full justify-start text-left font-normal h-11 rounded-xl border-border hover:bg-muted/50 transition-all',
            !selected && 'text-muted-foreground',
            triggerClassName
          )}
        >
          <CalendarIcon className='mr-2 h-4 w-4 text-muted-foreground shrink-0' />
          <span className='truncate'>
            {selected ? format(selected, 'dd-MM-yyyy') : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className={cn(
          'w-auto p-0 rounded-xl border-border shadow-xl',
          className
        )}
      >
        <Calendar
          mode='single'
          captionLayout='dropdown'
          fromYear={fromYear}
          toYear={toYear}
          selected={selected}
          onSelect={date => {
            onChange(date ? format(date, 'yyyy-MM-dd') : '')
            setOpen(false)
          }}
          disabled={min ? { before: min } : undefined}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}
